/**
 * FleetManager V3 - Dashboard Data Hook
 * Fetches real data from APIs for dashboard widgets
 */

import { useState, useEffect, useCallback } from 'react';
import {
  vehicleAPI,
  driverAPI,
  claimAPI,
  maintenanceAPI,
  fuelLogAPI,
  insuranceAPI,
  vehicleInsuranceAPI,
  inspectionAPI,
  kpiAPI,
  teamAPI,
  poolAPI
} from '../../api.js';
import { workingSiteAPI } from '../../api/admin.js';
import { buildTrulyInsuredIds } from '../../utils/insuranceStatus.js';
import { REFERENCE_CURRENCY } from '../../hooks/useOrgCurrency.js';
import { useProfile } from './useProfile.js';
import { useAuth } from '../context/AuthContext.jsx';

// Calculate days until a date
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

// i18n : ce hook ne fabrique AUCUN texte visible. Les alertes, échéances et
// activités portent des clés (`titleKey`, `actionKey`, `type` en code…) et
// leurs paramètres ; les widgets appellent t() au rendu, ce qui suit la
// bascule de langue sans refetch. L'horodatage relatif est rendu par
// fmt.relative() côté widget à partir de `timestamp`.

export function useDashboardData() {
  const [data, setData] = useState({
    vehicles: { total: 0, active: 0, maintenance: 0, outOfService: 0 },
    drivers: { total: 0 },
    driverAvailability: { total: 0, available: 0, unavailablePlanned: 0, unavailableUnplanned: 0, unknown: 0 },
    sites: { total: 0, byType: {} },
    teams: { total_teams: 0, active_teams: 0, total_members: 0, members_by_type: {} },
    claims: { open: 0, pending: 0, thisMonth: 0 },
    costs: { month: 0, trend: 0, currency: REFERENCE_CURRENCY, breakdown: { maintenance: 0, fuel: 0, insurance: 0, other: 0 } },
    alerts: [],
    deadlines: [],
    activities: [],
    kpis: {
      costs: { month: 0, trend: 0 },
      claims: { open: 0 },
      maintenance: { compliance: 0 },
      fuel: { consumption: 0, trend: 0 }
    },
    // Rempli uniquement pour le profil conducteur — voir la branche dédiée
    // ci-dessous. `null` tant que non chargé, jamais une donnée de flotte.
    pool: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { profile, isLoading: profileLoading } = useProfile();
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  // Le profil de présentation se résout après la connexion. Le rôle courant
  // interdit les lectures de parc même pendant ce délai ou une prévisualisation.
  const isDriverDashboard = ['driver', 'mechanic'].includes(user?.role)
    || profile?.id === 'conducteur';

  const fetchData = useCallback(async () => {
    if (authLoading || profileLoading || !isAuthenticated) return;
    setLoading(true);
    setError(null);

    // === CONDUCTEUR : son espace, pas le parc =============================
    // Le profil conducteur ne doit JAMAIS déclencher les fetches fleet-wide
    // ci-dessous (vehicleAPI.getAll(), insuranceAPI.getAll()…) : c'est
    // exactement la fuite constatée par JGS le 2026-09-13 (alertes de parc,
    // liste de tout le parc, coûts). Sa source unique est `poolAPI.getHome()`
    // (`GET /drivers/me/pool`), déjà scopé par le serveur (ADR-010 §3.3).
    if (isDriverDashboard) {
      try {
        const homeRes = await poolAPI.getHome();
        setData((prev) => ({
          ...prev,
          pool: homeRes.ok ? homeRes.data : { active_custody: null, reservations: [] }
        }));
      } catch (err) {
        // Un compte SANS fiche conducteur (un admin qui prévisualise le profil
        // conducteur, mesuré 2026-09-13) : ce n'est ni une panne ni une session
        // morte — le widget le dit, sans bruit dans la console ni bandeau d'erreur.
        if (err.code === 'NOT_DRIVER') {
          setData((prev) => ({ ...prev, pool: { not_linked: true, active_custody: null, reservations: [] } }));
        } else {
          console.error('Failed to fetch pool home for driver dashboard:', err);
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      // Fetch all data in parallel
      const [
        vehiclesRes,
        driversRes,
        claimsRes,
        maintenanceRes,
        fuelLogsRes,
        insuranceRes,
        vehicleInsurancesRes,
        inspectionsRes,
        sitesRes,
        teamsStatsRes
      ] = await Promise.all([
        vehicleAPI.getAll(),
        driverAPI.getAll(),
        claimAPI.getAll(),
        maintenanceAPI.getAll(),
        fuelLogAPI.getAll(),
        insuranceAPI.getAll(),
        vehicleInsuranceAPI.getAll().catch(() => ({ ok: false })),
        inspectionAPI.getAll(),
        workingSiteAPI.getStats(),
        teamAPI.getStats().catch(() => ({ ok: false }))
      ]);

      const vehicles = vehiclesRes.ok ? vehiclesRes.data : [];
      const drivers = driversRes.ok ? driversRes.data : [];
      const claims = claimsRes.ok ? claimsRes.data : [];
      const maintenanceRequests = maintenanceRes.ok ? maintenanceRes.data : [];
      const fuelLogs = fuelLogsRes.ok ? fuelLogsRes.data : [];
      const insuranceContracts = insuranceRes.ok ? insuranceRes.data : [];
      const vehicleInsurances = vehicleInsurancesRes.ok ? vehicleInsurancesRes.data : [];
      const inspections = inspectionsRes.ok ? inspectionsRes.data : [];
      const sites = sitesRes.ok ? sitesRes.data : [];
      const teamsStats = teamsStatsRes.ok && teamsStatsRes.data
        ? teamsStatsRes.data
        : { total_teams: 0, active_teams: 0, total_members: 0, members_by_type: {} };

      // === VEHICLE STATS ===
      // Only count operational vehicles (exclude retired/sold)
      const operationalVehicles = vehicles.filter(v => v.status !== 'retired' && v.status !== 'sold');
      const vehicleStats = {
        total: operationalVehicles.length,
        active: vehicles.filter(v => v.status === 'active').length,
        maintenance: vehicles.filter(v => v.status === 'maintenance' || v.status === 'in_maintenance').length,
        outOfService: vehicles.filter(v => v.status === 'out_of_service').length
      };

      // === DRIVER STATS ===
      // Only count active drivers (exclude inactive/terminated)
      const activeDrivers = drivers.filter(d => d.status !== 'inactive' && d.status !== 'terminated');
      const driverStats = {
        total: activeDrivers.length
      };

      // === DRIVER AVAILABILITY STATS ===
      const driverAvailabilityStats = {
        total: activeDrivers.length,
        available: activeDrivers.filter(d => d.availability_status === 'AVAILABLE').length,
        unavailablePlanned: activeDrivers.filter(d => d.availability_status === 'UNAVAILABLE_PLANNED').length,
        unavailableUnplanned: activeDrivers.filter(d => d.availability_status === 'UNAVAILABLE_UNPLANNED').length,
        unknown: activeDrivers.filter(d => !d.availability_status || d.availability_status === 'UNKNOWN').length
      };

      // === SITES STATS ===
      const activeSites = sites.filter(s => s.status === 'active');
      const sitesByType = activeSites.reduce((acc, site) => {
        const type = site.site_type || 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      const siteStats = {
        total: activeSites.length,
        byType: sitesByType
      };

      // === CLAIMS STATS ===
      // French status values from FleetManager workflow:
      // brouillon, declaration_envoyee, en_instruction, en_reparation, vei, attente_indemnisation, cloture
      const openStatuses = ['brouillon', 'declaration_prete', 'declaration_envoyee', 'en_instruction', 'expert_pending', 'expertise_realisee', 'rapport_recu', 'prise_en_charge_demandee', 'en_reparation', 'attente_facture_garage', 'vei', 'attente_indemnisation'];
      const pendingStatuses = ['en_instruction', 'expert_pending', 'expertise_realisee', 'rapport_recu', 'prise_en_charge_demandee', 'en_reparation', 'attente_facture_garage', 'vei', 'attente_indemnisation'];
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);

      const claimStats = {
        open: claims.filter(c => openStatuses.includes(c.status)).length,
        pending: claims.filter(c => pendingStatuses.includes(c.status)).length,
        thisMonth: claims.filter(c => new Date(c.created_at || c.event_date) >= thisMonth && c.status !== 'cloture').length
      };

      // === COSTS (single source of truth: KPI endpoint) ===
      let costStats = { month: 0, trend: 0, currency: REFERENCE_CURRENCY, breakdown: { maintenance: 0, fuel: 0, insurance: 0, other: 0 } };
      try {
        const kpiRes = await kpiAPI.getCostBreakdown('month');
        if (kpiRes.ok && kpiRes.data) {
          const b = kpiRes.data.breakdown;
          costStats = {
            month: Math.round(kpiRes.data.grandTotal || 0),
            trend: 0,
            // Group reporting contract: the backend sends `currency` (EUR by default)
            currency: kpiRes.data.currency || 'EUR',
            breakdown: {
              maintenance: Math.round(b.maintenance || 0),
              fuel: Math.round(b.fuel || 0),
              insurance: Math.round(b.insurance || 0),
              other: Math.round((b.leasing || 0) + (b.tax || 0) + (b.other || 0))
            }
          };
        }
      } catch (e) {
        console.warn('Could not fetch cost KPIs for dashboard:', e);
      }

      // === ALERTS ===
      const alerts = [];

      // Check for expired driver licenses
      drivers.forEach(driver => {
        const daysLeft = daysUntil(driver.license_expiry);
        if (daysLeft !== null && daysLeft <= 30) {
          alerts.push({
            id: `license-${driver.id}`,
            type: daysLeft <= 0 ? 'critical' : 'warning',
            category: 'license',
            titleKey: daysLeft <= 0 ? 'dashboard.alerts.licenseExpired' : 'dashboard.alerts.licenseExpiring',
            subtitleKey: daysLeft <= 0 ? 'dashboard.alerts.driverExpired' : 'dashboard.alerts.driverDaysLeft',
            subtitleParams: { name: `${driver.first_name} ${driver.last_name}`, count: daysLeft },
            actionKey: 'dashboard.alerts.actions.view',
            entityId: driver.id,
            entityType: 'driver'
          });
        }
      });

      // Check for active vehicles without truly-active insurance.
      // A VI on a terminated/expired/suspended contract does NOT count.
      // (see utils/insuranceStatus.js)
      const activeVehicles = vehicles.filter(v => v.status === 'active');
      const insuredVehicleIds = buildTrulyInsuredIds(vehicleInsurances, insuranceContracts);
      const uninsuredVehicles = activeVehicles.filter(v => !insuredVehicleIds.has(v.id));
      if (uninsuredVehicles.length > 0) {
        const sample = uninsuredVehicles.slice(0, 3).map(v => v.license_plate || `#${v.id}`).join(', ');
        const more = uninsuredVehicles.length > 3 ? `, +${uninsuredVehicles.length - 3}` : '';
        alerts.push({
          id: 'vehicles-uninsured',
          type: 'critical',
          category: 'insurance',
          titleKey: 'dashboard.alerts.uninsuredVehicles',
          titleParams: { count: uninsuredVehicles.length },
          subtitle: `${sample}${more}`,
          actionKey: 'dashboard.alerts.actions.attach',
          entityType: 'insurance'
        });
      }

      // Check for insurance expiry
      insuranceContracts.forEach(contract => {
        const daysLeft = daysUntil(contract.expiry_date);
        if (daysLeft !== null && daysLeft <= 30 && daysLeft > 0) {
          alerts.push({
            id: `insurance-${contract.id}`,
            type: daysLeft <= 7 ? 'critical' : 'warning',
            category: 'insurance',
            titleKey: 'dashboard.alerts.insuranceExpiring',
            subtitleKey: 'dashboard.alerts.insurerDaysLeft',
            subtitleParams: { name: contract.insurer_name, count: daysLeft },
            actionKey: 'dashboard.alerts.actions.view',
            entityId: contract.id,
            entityType: 'insurance'
          });
        }
      });

      // Check for overdue maintenance
      const overdueMaintenance = maintenanceRequests.filter(m =>
        m.status === 'approved' || m.status === 'submitted'
      );
      if (overdueMaintenance.length > 0) {
        alerts.push({
          id: 'maintenance-pending',
          type: 'warning',
          category: 'maintenance',
          titleKey: 'dashboard.alerts.maintenancePending',
          subtitleKey: 'dashboard.alerts.requestsToHandle',
          subtitleParams: { count: overdueMaintenance.length },
          actionKey: 'dashboard.alerts.actions.schedule',
          entityType: 'maintenance'
        });
      }

      // Check for undeclared claims (brouillon status)
      const draftClaims = claims.filter(c => c.status === 'brouillon');
      if (draftClaims.length > 0) {
        alerts.push({
          id: 'claims-draft',
          type: 'warning',
          category: 'claim',
          titleKey: 'dashboard.alerts.claimUndeclared',
          subtitleKey: 'dashboard.alerts.draftClaims',
          subtitleParams: { count: draftClaims.length },
          actionKey: 'dashboard.alerts.actions.declare',
          entityType: 'claim'
        });
      }

      // Fetch SLA overdue alerts from backend (business days calculation)
      try {
        const slaRes = await claimAPI.getAlerts();
        if (slaRes.ok && slaRes.data?.length > 0) {
          const criticalAlerts = slaRes.data.filter(a => a.type === 'critical');
          const warningAlerts = slaRes.data.filter(a => a.type === 'warning');

          if (criticalAlerts.length > 0) {
            alerts.push({
              id: 'claims-sla-critical',
              type: 'critical',
              category: 'claim',
              titleKey: 'dashboard.alerts.claimsOverdue',
              titleParams: { count: criticalAlerts.length },
              subtitle: criticalAlerts.map(a => `${a.claim_number}: ${a.message}`).slice(0, 3).join(' | '),
              actionKey: 'dashboard.alerts.actions.view',
              entityType: 'claim'
            });
          }
          if (warningAlerts.length > 0) {
            alerts.push({
              id: 'claims-sla-warning',
              type: 'warning',
              category: 'claim',
              titleKey: 'dashboard.alerts.claimsNearDeadline',
              titleParams: { count: warningAlerts.length },
              subtitle: warningAlerts.map(a => `${a.claim_number}: ${a.message}`).slice(0, 3).join(' | '),
              actionKey: 'dashboard.alerts.actions.view',
              entityType: 'claim'
            });
          }
        }
      } catch (e) {
        console.warn('Could not fetch claim SLA alerts:', e);
      }

      // Sort alerts: critical first, then by type
      alerts.sort((a, b) => {
        if (a.type === 'critical' && b.type !== 'critical') return -1;
        if (a.type !== 'critical' && b.type === 'critical') return 1;
        return 0;
      });

      // === DEADLINES ===
      // Horizon of 60 days, filters out anything already past (daysLeft > 0).
      const deadlines = [];

      // CT — read vehicles.ct_expiry_date directly. The previous impl derived
      // the next CT date from the inspections table (lastPassedDate + 1 year),
      // which misses every vehicle whose CT was recorded only on the vehicle
      // row (e.g. from an Excel import or the entry wizard).
      vehicles.forEach(vehicle => {
        const daysLeft = daysUntil(vehicle.ct_expiry_date);
        if (daysLeft !== null && daysLeft <= 60 && daysLeft > 0) {
          deadlines.push({
            id: `ct-${vehicle.id}`,
            type: 'ct',
            vehicle: `${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
            plate: vehicle.license_plate,
            daysLeft
          });
        }
      });

      // Driver license deadlines
      drivers.forEach(driver => {
        const daysLeft = daysUntil(driver.license_expiry);
        if (daysLeft !== null && daysLeft <= 60 && daysLeft > 0) {
          deadlines.push({
            id: `license-${driver.id}`,
            type: 'license',
            vehicle: `${driver.first_name} ${driver.last_name}`,
            plate: null,
            plateKey: 'dashboard.deadlines.driver',
            daysLeft
          });
        }
      });

      // Insurance deadlines (on the contract envelope — vehicle-level end dates
      // live on vehicle_insurance and are not modeled here).
      insuranceContracts.forEach(contract => {
        const daysLeft = daysUntil(contract.expiry_date);
        if (daysLeft !== null && daysLeft <= 60 && daysLeft > 0) {
          deadlines.push({
            id: `insurance-${contract.id}`,
            type: 'insurance',
            vehicle: contract.insurer_name,
            plate: contract.policy_number,
            daysLeft
          });
        }
      });

      // Maintenance deadlines. Canonical lean-3-step enum is `planned`; the
      // legacy `approved`/`scheduled` values are kept for compatibility with
      // rows that predate the 2026-04-21 migration.
      const MAINT_SCHEDULED_STATUSES = new Set(['planned', 'approved', 'scheduled']);
      // Le code de maintenance_request.type est émis tel quel (oil_change,
      // tire_rotation, …) : le widget le traduit via dashboard.deadlines.types.*
      // avec repli sur 'maintenance' pour un code inconnu.
      const MAINT_TYPE_CODES = new Set([
        'oil_change', 'tire_rotation', 'brake_service', 'engine_repair', 'transmission',
        'electrical', 'bodywork', 'inspection', 'emergency', 'other',
      ]);
      maintenanceRequests
        .filter(m => MAINT_SCHEDULED_STATUSES.has(m.status))
        .forEach(maint => {
          const vehicle = vehicles.find(v => v.id === maint.vehicle_id);
          if (!vehicle) return;
          // Prefer the real scheduled_date; fall back to a 7-day default only
          // if the request was created without one.
          const daysLeft = maint.scheduled_date
            ? daysUntil(maint.scheduled_date)
            : 7;
          if (daysLeft === null || daysLeft > 60 || daysLeft <= 0) return;
          deadlines.push({
            id: `maint-${maint.id}`,
            type: MAINT_TYPE_CODES.has(maint.type) ? maint.type : 'maintenance',
            vehicle: `${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
            plate: vehicle.license_plate,
            daysLeft
          });
        });

      // Sort by days left
      deadlines.sort((a, b) => a.daysLeft - b.daysLeft);

      // === ACTIVITIES ===
      // Combine recent items from various sources
      const activities = [];

      // Recent vehicles
      vehicles
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 2)
        .forEach(v => {
          activities.push({
            id: `vehicle-${v.id}`,
            actionKey: 'dashboard.activity.vehicleUpdated',
            actionParams: { plate: v.license_plate },
            target: 'vehicles',
            timestamp: new Date(v.updated_at)
          });
        });

      // Recent maintenance
      maintenanceRequests
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 2)
        .forEach(m => {
          const vehicle = vehicles.find(v => v.id === m.vehicle_id);
          activities.push({
            id: `maint-${m.id}`,
            actionKey: m.status === 'completed' ? 'dashboard.activity.maintenanceCompleted' : 'dashboard.activity.maintenancePlanned',
            actionParams: { context: vehicle ? 'vehicle' : undefined, plate: vehicle?.license_plate },
            target: 'maintenance',
            timestamp: new Date(m.created_at)
          });
        });

      // Recent claims
      claims
        .sort((a, b) => new Date(b.created_at || b.event_date) - new Date(a.created_at || a.event_date))
        .slice(0, 2)
        .forEach(c => {
          const vehicle = vehicles.find(v => v.id === c.vehicle_id);
          activities.push({
            id: `claim-${c.id}`,
            actionKey: 'dashboard.activity.claimDeclared',
            actionParams: { context: vehicle ? 'vehicle' : undefined, vehicle: vehicle ? `${vehicle.make} ${vehicle.model}` : '' },
            target: 'claims',
            timestamp: new Date(c.created_at || c.event_date)
          });
        });

      // Recent drivers
      drivers
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 1)
        .forEach(d => {
          activities.push({
            id: `driver-${d.id}`,
            actionKey: 'dashboard.activity.driverAdded',
            actionParams: { name: `${d.first_name} ${d.last_name}` },
            target: 'drivers',
            timestamp: new Date(d.created_at)
          });
        });

      // Sort by timestamp and take top 5 — `timestamp` is kept: the widget
      // renders it with fmt.relative() in the active language.
      activities.sort((a, b) => b.timestamp - a.timestamp);
      const topActivities = activities.slice(0, 5);

      // === KPIs ===
      const totalMaintenance = maintenanceRequests.length;
      const completedMaintenance = maintenanceRequests.filter(m => m.status === 'completed').length;
      const maintenanceCompliance = totalMaintenance > 0
        ? Math.round((completedMaintenance / totalMaintenance) * 100)
        : 100;

      // Calculate average fuel consumption (L/100km)
      const fuelConsumption = fuelLogs.length > 0
        ? fuelLogs.reduce((sum, f) => sum + (f.liters || 0), 0) / fuelLogs.length
        : 0;

      const kpis = {
        costs: { month: costStats.month, trend: 0, currency: costStats.currency },
        claims: { open: claimStats.open },
        maintenance: { compliance: maintenanceCompliance },
        fuel: { consumption: Math.round(fuelConsumption * 10) / 10, trend: 0 }
      };

      setData({
        vehicles: vehicleStats,
        drivers: driverStats,
        driverAvailability: driverAvailabilityStats,
        sites: siteStats,
        teams: teamsStats,
        claims: claimStats,
        costs: costStats,
        alerts: alerts.slice(0, 4), // Limit to 4 alerts
        deadlines: deadlines.slice(0, 4), // Limit to 4 deadlines
        activities: topActivities,
        kpis
      });

    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isDriverDashboard, authLoading, profileLoading, isAuthenticated]);

  // Fetch data on mount (and when the profile resolves conducteur vs. flotte)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refresh: fetchData
  };
}

export default useDashboardData;
